<template>
  <b-card
    id="card__content"
    :class="['grid__card', (isPlaceholder || isHidden) && 'placeholder__card' ]"
    :style="{...styles}"
    :header-class=" ((isPlaceholder || isHidden) && isModal) ? 'placeholder__card--header' : ''"
  >
    <template #header>
      <div class="title__wrapper">
        <font-awesome-icon
          v-if="isHidden"
          class="text-warning"
          :icon="['fas', 'eye-slash']"
        />
        <h4
          :class="[(isPlaceholder || isHidden) ? 'placeholder__title' : '']"
          v-html="formattedTitle"
        />
      </div>
      <div
        v-if="!isModal && !isFilePreviewer"
        class="actions"
      >
        <b-button
          variant="outlined"
          class="icon"
          @click="openCustomisationModal"
        >
          <font-awesome-icon :icon="['fas', 'cog']" />
        </b-button>
        <b-button
          variant="outlined"
          class="icon"
          @click="deleteSection"
        >
          <font-awesome-icon :icon="['fas', 'close']" />
        </b-button>
      </div>
    </template>
    <grid-layout
      :id="containerId"
      :ref="containerId"
      :margin="[10, 15]"
      :layout="layout"
      :col-num="12"
      :vertical-compact="false"
      :use-css-transform="false"
      :row-height="55"
      :is-draggable="isEditable"
      :is-resizable="isEditable"
      :class="[isModal && isHidden ? 'hide__border' : '']"
    >
      <template v-if="layout.length">
        <designer-grid-layout-item
          v-for="item in layout"
          :key="item.fieldGuid"
          :layout-id="containerId"
          :section-name="title"
          :item="item"
          :is-file-previewer="isFilePreviewer"
        />
      </template>
      <no-content
        v-else
        :show="!layout.length"
        text="You do not have any fields related to this section yet"
      />
    </grid-layout>
  </b-card>
</template>

<script>
import {GridLayout} from 'vue-grid-layout';
import DesignerGridLayoutItem from '@/core/components/complex-table/DesignerGridLayoutItem.vue';
import {createNamespacedHelpers} from 'vuex';
import InputWidget from '@/core/components/complex-table/components/InputWidget.vue';
import {throttle} from 'lodash';
import NoContent from '@/core/components/NoContent.vue';
import ColorPickerComponent from '@/modules/DataManagement/views/designer/components/ColorPickerComponent.vue';
import DesignerCustomisationsModal
  from '@/modules/DataManagement/views/designer/components/modals/DesignerCustomisationsModal.vue';
import {highlightText} from '@/core/utils';

const {
  mapState: designerMapState,
  mapActions: designerMapActions,
} = createNamespacedHelpers('designer');

export default {
  name: 'DesignerGridLayout',
  components: {
    DesignerCustomisationsModal,
    ColorPickerComponent,
    NoContent,
    InputWidget,
    DesignerGridLayoutItem,
    GridLayout,
  },
  props: {
    containerId: String,
    layout: Array,
    title: String,
    isPlaceholder: Boolean,
    isHidden: Boolean,
    styles: {
      type: Object || null,
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    isFilePreviewer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mouseXY: {x: null, y: null},
      ref: null,
      container: null,
      stylesObj: null,
    };
  },
  computed: {
    ...designerMapState({
      defaultForm: state => state.item,
      id: state => state.dndSectionId,
      isModal: state => state.isModalOpened,
      isCustomisationModalOpened: state => state.customisationsModal.isOpen,
      searchValue: state => state.searchValue,
    }),
    formattedTitle() {
      return highlightText(this.searchValue, this.title);
    },
  },
  methods: {
    ...designerMapActions([
      'setMouseCoords',
      'setDndSectionId',
      'setAddNewSectionModalState',
      'deleteSectionFromList',
      'setCustomisationModalState',
    ]),
    async deleteSection() {
      const confirm = await this.$confirm(
        'Delete section',
        'Are you sure you want to delete the current section?',
      );
      confirm && this.deleteSectionFromList(this.containerId);
    },
    openCustomisationModal() {
      this.setCustomisationModalState({
        isOpen: true,
        isEditMode: true,
        title: this.title,
        sectionId: this.containerId,
      });
    },
    getDraggableItemAxesParams: throttle(function (e) {
      const {clientX, clientY} = e;
      this.setMouseCoords({x: clientX, y: clientY});
      this.setDndSectionId(this.containerId);
    }, 100),
  },
  mounted() {
    if (!this.isModal) {
      this.container = this.$refs[this.containerId].$el;
      this.container.addEventListener('dragover', this.getDraggableItemAxesParams);

      this.$emit('get-parent-refs', {
        id: this.containerId,
        ref: this.$refs[this.containerId],
      });
    }
  },
  destroyed() {
    if (!this.isModal && this.container) {
      this.container.removeEventListener('dragover', this.getDraggableItemAxesParams);
    }
  },
};
</script>

<style scoped lang="scss">

.grid__card {
  border-radius: 6px;
  border-width: 1px;

  & .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 15px;
    background-color: transparent;
  }

  & .card-body {
    padding: 10px;
  }

  &:not(.placeholder__card), & .card-title {
    border-bottom: 1px solid #eee;
  }

  & .card-title {
    padding-bottom: 10px;
    margin-bottom: 0;
  }

  &:not(:last-child) {
    margin-bottom: 20px;
  }
}

.placeholder__card {
  & .placeholder__card--header {
    display: none;
  }
}

.placeholder__title {
  margin-bottom: 0;
  font-style: italic;
  font-weight: 200;
  color: #ccc
}

.vue-grid-layout {
  min-height: 100px;
  background: transparent;
  height: 100%;
  width: 100%;
  border: 1px solid #eee;

  &.hide__border {
    border: none;
  }
}

.vue-grid-item:has(.show),
.vue-grid-item:has(.multiselect__content-wrapper){
  z-index: 1000;
}
//.vue-recycle-scroller__item-view:has(.multiselect--active) {
//  z-index: 100;
//}

.vue-grid-item .resizing {
  opacity: 1;
}

.vue-grid-item.static {
  background: #0F355E;
}

.vue-grid-item .text {
  font-size: 24px;
  text-align: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: 100%;
  width: 100%;
}

.vue-grid-item .no-drag {
  height: 100%;
  width: 100%;
}

.vue-grid-item .minMax {
  font-size: 12px;
}

.vue-grid-item .add {
  cursor: pointer;
}

.icon {
  color: currentColor;
}

.title__wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>